import { useCallback, useMemo, useRef, useState, type RefObject } from 'react';
import { ChatBubbleBottomCenterTextIcon, FaceSmileIcon } from '@heroicons/react/24/outline';
import { BoltIcon } from '@heroicons/react/20/solid';
import { useDmQuickReplies } from '../../../../../hooks/useDmQuickReplies';
import { usePopoverDismiss } from '../../../../../hooks/usePopoverDismiss';
import type { DmQuickReply, DmQuickReplyMedia } from '../../../../../services/dmQuickReplies';
import { QuickRepliesPopover } from '../../../../quick-replies/QuickRepliesPopover';
import { MarkupScanPhotoButton } from '../scanMarkup/MarkupScanPhotoButton';
import { AttachFileButton } from './AttachFileButton';
import { COMPOSER_ICON_BADGE_CLASS, ComposerIconButton } from './ComposerIconButton';
import { EmojiPickerPopover } from './EmojiPickerPopover';
import type { AttachmentNotice } from './useComposerAttachment';

type ComposerPopover = 'emoji' | 'quick-replies';

const QUICK_REPLIES_ERROR_FALLBACK = 'Could not load quick replies.';

function toErrorMessage(error: unknown): string | null {
    if (!error) return null;
    return error instanceof Error ? error.message : QUICK_REPLIES_ERROR_FALLBACK;
}

interface ComposerToolsProps {
    patientId: string;
    // The composer box: the quick replies popover spans its width so it fits narrow panels.
    anchorRef: RefObject<HTMLElement>;
    onInsert: (text: string) => void;
    onAttachFiles: (files: File[]) => void;
    onAttachFile: (file: Blob, name: string) => boolean;
    attachNotice: AttachmentNotice | null;
    onQuickReplyMedia: (media: DmQuickReplyMedia) => void;
}

function ComposerTools({
    patientId,
    anchorRef,
    onInsert,
    onAttachFiles,
    onAttachFile,
    attachNotice,
    onQuickReplyMedia
}: ComposerToolsProps) {
    const [openPopover, setOpenPopover] = useState<ComposerPopover | null>(null);
    const emojiButtonRef = useRef<HTMLButtonElement>(null);
    const emojiPickerRef = useRef<HTMLDivElement>(null);
    const quickRepliesButtonRef = useRef<HTMLButtonElement>(null);

    const quickRepliesOpen = openPopover === 'quick-replies';
    const quickReplies = useDmQuickReplies(patientId, quickRepliesOpen);

    const close = useCallback(() => setOpenPopover(null), []);
    const toggle = (popover: ComposerPopover) =>
        setOpenPopover((prev) => (prev === popover ? null : popover));

    const emojiInsideRefs = useMemo(() => [emojiPickerRef, emojiButtonRef], []);
    usePopoverDismiss({
        open: openPopover === 'emoji',
        onClose: close,
        insideRefs: emojiInsideRefs
    });

    const handleEmojiSelect = useCallback(
        (emoji: { native: string }) => onInsert(emoji.native ?? ''),
        [onInsert]
    );

    const handleQuickReplyPick = useCallback(
        (reply: DmQuickReply) => {
            close();
            onInsert(reply.content);
            if (reply.media) onQuickReplyMedia(reply.media);
        },
        [close, onInsert, onQuickReplyMedia]
    );

    return (
        <div className="flex min-w-0 items-center gap-0.5">
            <ComposerIconButton
                ref={emojiButtonRef}
                label="Insert emoji"
                active={openPopover === 'emoji'}
                onClick={() => toggle('emoji')}
            >
                <FaceSmileIcon className="w-5 h-5" />
            </ComposerIconButton>
            {openPopover === 'emoji' && (
                <EmojiPickerPopover
                    triggerRef={emojiButtonRef}
                    popoverRef={emojiPickerRef}
                    onSelect={handleEmojiSelect}
                />
            )}
            <ComposerIconButton
                ref={quickRepliesButtonRef}
                label="Quick replies"
                active={quickRepliesOpen}
                onClick={() => toggle('quick-replies')}
            >
                {/* A chat bubble with a small bolt: a message, but a canned one. */}
                <span className="relative inline-flex">
                    <ChatBubbleBottomCenterTextIcon className="w-5 h-5" />
                    <BoltIcon
                        className={`absolute -right-1 -bottom-1 w-3 h-3 text-amber-500 ${COMPOSER_ICON_BADGE_CLASS}`}
                    />
                </span>
            </ComposerIconButton>
            <AttachFileButton onFiles={onAttachFiles} />
            <MarkupScanPhotoButton
                patientId={patientId}
                notice={attachNotice}
                onAttach={onAttachFile}
                onOpen={close}
            />
            {quickRepliesOpen && (
                <QuickRepliesPopover
                    anchorRef={anchorRef}
                    buttonRef={quickRepliesButtonRef}
                    replies={quickReplies.data}
                    isFetching={quickReplies.isFetching}
                    errorMessage={toErrorMessage(quickReplies.error)}
                    onRetry={() => void quickReplies.refetch()}
                    onPick={handleQuickReplyPick}
                    onClose={close}
                />
            )}
        </div>
    );
}

export { ComposerTools };
